import React, { useState, useEffect } from 'react';
import { ChevronDown, User } from 'lucide-react';
import { TrelloMember, trelloService } from '../lib/trello';

interface MemberSelectorProps {
  value?: string; // Member ID
  onChange: (memberId: string | undefined) => void;
  placeholder?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export default function MemberSelector({ 
  value, 
  onChange, 
  placeholder = "Select assignee",
  isOpen: externalIsOpen,
  onToggle: externalOnToggle
}: MemberSelectorProps) {
  const [members, setMembers] = useState<TrelloMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    if (!trelloService.isConnected()) return;
    
    setLoading(true);
    try {
      const memberList = await trelloService.getMembers();
      setMembers(memberList);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedMember = members.find(member => member.id === value);

  const handleSelect = (member: TrelloMember | null) => {
    onChange(member?.id);
    if (externalOnToggle) {
      externalOnToggle(); // Close dropdown
    } else {
      setInternalIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          if (externalOnToggle) {
            externalOnToggle();
          } else {
            setInternalIsOpen(!internalIsOpen);
          }
        }}
        className="w-full flex items-center justify-between p-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-left"
      >
        <div className="flex items-center space-x-2 flex-1">
          {selectedMember ? (
            <>
              {selectedMember.avatarUrl ? (
                <img 
                  src={`${selectedMember.avatarUrl}/30.png`} 
                  alt={selectedMember.fullName}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-600" />
                </div>
              )}
              <span className="text-gray-900">
                {selectedMember.fullName || selectedMember.username}
              </span>
            </>
          ) : (
            <>
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-gray-500">{placeholder}</span>
            </>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="p-3 text-center text-gray-500">
              <div className="inline-block w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin mr-2"></div>
              Loading members...
            </div>
          ) : members.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              No members found
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full text-left p-3 hover:bg-gray-50 flex items-center space-x-2"
              >
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-500">No assignee</span>
              </button>
              {members.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => handleSelect(member)}
                  className="w-full text-left p-3 hover:bg-gray-50 flex items-center space-x-2"
                >
                  {member.avatarUrl ? (
                    <img 
                      src={`${member.avatarUrl}/30.png`} 
                      alt={member.fullName}
                      className="w-6 h-6 rounded-full"
                    />
                  ) : (
                    <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {member.fullName || member.username}
                    </div>
                    {member.fullName && member.username && (
                      <div className="text-sm text-gray-500">@{member.username}</div>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
} 